import { useMutation } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { Download, Settings2, Sparkles, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { z } from 'zod'

import { exportVocabulary, importVocabulary, useVocabulary } from '@/api/vocab'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { toast } from '@/components/ui/toast'
import { ProposedKindsDialog } from '@/features/vocabulary/ProposedKindsDialog'
import { VocabularyTable } from '@/features/vocabulary/VocabularyTable'
import { Domain } from '@/schema'
import { useVocabStore } from '@/stores/vocabStore'

/** `/settings/vocabulary?domain=` — one table per vocabulary domain. */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const vocabularySearchSchema = z.object({
  /** A vocab domain key (`region_kind`, `anchor_kind`, `theme`, `blocked_word`, …); validated against the loaded vocabulary. */
  domain: lenient(z.string()),
  /** Show deprecated rows too. */
  all: lenient(z.coerce.boolean()),
})

export type VocabularySearch = z.infer<typeof vocabularySearchSchema>

export function validateVocabularySearch(input: Record<string, unknown>): VocabularySearch {
  return vocabularySearchSchema.parse(input)
}

const DOMAIN_LABEL: Record<string, string> = {
  region_kind: 'Regions',
  anchor_kind: 'Anchors',
  portal_kind: 'Ways out',
  path_kind: 'Paths',
  surface: 'Surfaces',
  action: 'Actions',
  affordance: 'Affordances',
  tag: 'Tags',
  emotion: 'Feelings',
  theme: 'Story themes',
  blocked_word: 'Blocked words',
  skin_suffix: 'Skin suffixes',
  clip_modifier: 'Clip suffixes',
}

const route = getRouteApi('/settings/vocabulary')

/**
 * The vocabulary is the only place kinds are defined (§4.4) — the editor
 * legend, the `1–9` shortcuts, the AI prompt glossary and the story
 * schema enums all read these tables. Nothing here needs a deploy: adding
 * a theme changes the next story, adding a region kind changes the next
 * annotation.
 */
export function SettingsVocabularyPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()

  const query = useVocabulary({ includeDisabled: true })
  const rev = useVocabStore((s) => s.rev)

  const [proposedOpen, setProposedOpen] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const domain = (Domain.options as readonly string[]).includes(search.domain ?? '') ? (search.domain as Domain) : 'region_kind'
  const showAll = search.all ?? false

  const doExport = useMutation({
    mutationFn: () => exportVocabulary(),
    onSuccess: (v) => {
      const blob = new Blob([JSON.stringify(v, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `story-studio-vocabulary-rev${v.rev}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Saved a copy of the vocabulary')
    },
    onError: (e) => toast.error('Could not export', { description: e instanceof Error ? e.message : String(e) }),
  })

  const doImport = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text()
      return importVocabulary(JSON.parse(text) as Record<string, unknown>)
    },
    onSuccess: (v) => {
      toast.success('Vocabulary merged', { description: `Now at revision ${v.rev}.` })
      void query.refetch()
    },
    onError: (e) => toast.error('Could not import that file', { description: e instanceof Error ? e.message : String(e) }),
  })

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Vocabulary</h1>
          <p className="text-sm text-ink-2">
            The words the studio knows. Everything — the editor, Claude&apos;s prompts, the story rules — reads this list.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral">revision {rev || query.data?.rev || 0}</Badge>
          <Button variant="ghost" onClick={() => setProposedOpen(true)}>
            <Sparkles aria-hidden />
            Suggested
          </Button>
          <Button variant="ghost" onClick={() => doExport.mutate()} loading={doExport.isPending}>
            <Download aria-hidden />
            Export
          </Button>
          <Button variant="ghost" onClick={() => fileInput.current?.click()} loading={doImport.isPending}>
            <Upload aria-hidden />
            Import
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) doImport.mutate(file)
              e.target.value = ''
            }}
          />
          <Button asChild variant="secondary">
            <Link to="/settings">
              <Settings2 aria-hidden />
              Settings
            </Link>
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {Domain.options.map((d) => (
          <Chip key={d} size="sm" selected={domain === d} onClick={() => void navigate({ search: (prev) => ({ ...prev, domain: d }), replace: true })}>
            {DOMAIN_LABEL[d] ?? d}
          </Chip>
        ))}
        <Chip
          size="sm"
          className="ml-auto"
          selected={showAll}
          onClick={() => void navigate({ search: (prev) => ({ ...prev, all: showAll ? undefined : true }), replace: true })}
        >
          Show retired
        </Chip>
      </div>

      {query.isPending ? (
        <p className="card px-4 py-10 text-center text-sm text-ink-2">Loading the vocabulary…</p>
      ) : query.isError ? (
        <div className="card px-4 py-10 text-center" role="alert">
          <p className="text-sm text-ink-2">Could not load the vocabulary. {query.error instanceof Error ? query.error.message : ''}</p>
          <Button className="mt-3" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <VocabularyTable domain={domain} showAll={showAll} />
      )}

      <ProposedKindsDialog open={proposedOpen} onOpenChange={setProposedOpen} defaultDomain={domain} />
    </div>
  )
}
